import { useEffect, useRef, useState } from 'react';
import { timeTrackingAPI } from '../lib/api';
import { toast } from 'sonner';

// Erinnert an Ein-/Ausstempeln, ausgerichtet an der durchschnittlichen
// (üblichen) Stempelzeit des Mitarbeiters. In-App-Toast + optional
// Browser-Desktop-Benachrichtigung. Rendert keine sichtbare UI.
const REMIND_AFTER_MIN = 10; // X Min. nach üblicher Einstempelzeit
const IN_WINDOW_MIN = 180; // Einstempel-Erinnerung nur bis 3 h nach üblicher Zeit
const OUT_WINDOW_MIN = 240; // Ausstempel-Erinnerung bis 4 h nach üblicher Zeit

const nowMinutes = () => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); };
const todayKey = () => new Date().toISOString().slice(0, 10);
const hhmmToMin = (s) => { if (!s) return null; const [h, m] = s.split(':').map(Number); return h * 60 + m; };
const alreadyReminded = (type) => localStorage.getItem(`clockReminder:${type}:${todayKey()}`) === '1';
const markReminded = (type) => localStorage.setItem(`clockReminder:${type}:${todayKey()}`, '1');

function notify(title, body) {
  toast(title, { description: body, duration: 10000 });
  try {
    if ('Notification' in window && Notification.permission === 'granted') {
      // eslint-disable-next-line no-new
      new Notification(title, { body });
    }
  } catch { /* ignore */ }
}

export const ClockReminder = () => {
  const statusRef = useRef(null);
  const [, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'denied');

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(setPerm).catch(() => {});
    }
  }, []);

  useEffect(() => {
    let timer;
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const r = await timeTrackingAPI.getReminderStatus();
        if (!cancelled) statusRef.current = r.data;
      } catch {
        statusRef.current = null;
      }
    };

    const check = () => {
      const s = statusRef.current;
      if (!s || !s.enabled) return;

      // Pausen-Erinnerung: läuft unabhängig von Urlaub/Feiertag, da eine
      // laufende Pause bedeutet, dass die Person aktuell eingestempelt arbeitet.
      const breakThreshold = Number(s.break_reminder_minutes);
      if (s.on_break && s.break_started_at && Number.isFinite(breakThreshold) && breakThreshold > 0) {
        const startedMs = new Date(s.break_started_at).getTime();
        if (Number.isFinite(startedMs)) {
          const elapsed = Math.floor((Date.now() - startedMs) / 60000);
          const breakKey = `break:${s.break_started_at}`;
          if (elapsed >= breakThreshold && !alreadyReminded(breakKey)) {
            markReminded(breakKey);
            notify('Pause läuft noch', `Deine Pause läuft seit über ${breakThreshold} Minuten. Nicht vergessen, sie zu beenden.`);
          }
        }
      }

      if (s.off_today) return; // Urlaub/Abwesenheit → keine Stempel-Erinnerung
      const remindAfter = Number.isFinite(s.threshold_minutes) ? s.threshold_minutes : REMIND_AFTER_MIN;
      const n = nowMinutes();
      const inAvg = hhmmToMin(s.avg_clock_in);
      const outAvg = hhmmToMin(s.avg_clock_out);

      if (inAvg != null && !s.is_clocked_in && !s.clocked_in_today && !alreadyReminded('in')
          && n >= inAvg + remindAfter && n <= inAvg + IN_WINDOW_MIN) {
        markReminded('in');
        notify('Einstempeln nicht vergessen', `Du stempelst üblicherweise gegen ${s.avg_clock_in} Uhr ein und bist heute noch nicht eingestempelt.`);
      }

      if (outAvg != null && s.is_clocked_in && !alreadyReminded('out')
          && n >= outAvg && n <= outAvg + OUT_WINDOW_MIN) {
        markReminded('out');
        notify('Ausstempeln nicht vergessen', `Deine übliche Ausstempelzeit (~${s.avg_clock_out} Uhr) ist erreicht – du bist noch eingestempelt.`);
      }
    };

    (async () => {
      await fetchStatus();
      check();
      if (!cancelled && statusRef.current && statusRef.current.enabled) {
        timer = setInterval(async () => { await fetchStatus(); check(); }, 60000);
      }
    })();

    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, []);

  return null;
};
